import * as z from "zod";

export enum TeamworkCommerceReturnPurposeTextSource {
  REASON = "reason",
  FIRST_REASON_CODE = "first_reason_code",
  LAST_REASON_CODE = "last_reason_code",
}

export const DEFAULT_TEAMWORK_COMMERCE_RETURN_PURPOSE_TEXT_SOURCE =
  TeamworkCommerceReturnPurposeTextSource.LAST_REASON_CODE;

export const TEAMWORK_COMMERCE_GIFT_CARD_LIMIT = 2000;

export const DEFAULT_TEAMWORK_COMMERCE_RETURN_PURPOSE_TEXT_FALLBACK = "Return";

export enum TeamworkCommerceDocumentKind {
  RECEIPT = "receipt",
  WORKSHEET = "worksheet",
}

export enum TeamworkCommerceStoreCreditIssuer {
  PLATFORM = "platform",
  TEAMWORK = "teamwork",
}

export enum TeamworkCommerceItemIdentifierSource {
  VARIANT_ID = "variant_id",
  SKU = "sku",
}

export enum TeamworkCommerceItemSetting {
  EXTERNAL_ID = "ExternalId",
  UPC = "Upc",
  PLU = "Plu",
  ID = "Id",
}

export enum TeamworkCommerceSalesOrderNumberSource {
  ORDER_ID = "order_id",
  ORDER_NAME = "order_name",
  ORDER_NUMBER = "order_number",
}

const code = z.string().trim().min(1);
const itemMatching = {
  itemIdentifierSource: z.enum(TeamworkCommerceItemIdentifierSource),
  itemSetting: z.enum(TeamworkCommerceItemSetting),
};

export const TeamworkCommerceReceiptSettingsSchema = z.object({
  ...itemMatching,
  locationCode: code,
  fillFromLocationCode: code.optional(),
  salesOrderNumberSource: z.enum(TeamworkCommerceSalesOrderNumberSource),
  salesOrderNumberMetadataKey: code.optional(),
  refundPaymentMethodCode: code,
  platformStoreCreditPaymentMethodCode: code.optional(),
  adjustmentChargeCode: code.optional(),
});

export type TeamworkCommerceReceiptSettings = z.infer<
  typeof TeamworkCommerceReceiptSettingsSchema
>;

export const TeamworkCommerceWorksheetSettingsSchema = z.object({
  ...itemMatching,
  locationCode: code,
  returnConditionCode: code,
  itemChargeCode: code.optional(),
  exchangeShippingMethodCode: code.optional(),
});

export type TeamworkCommerceWorksheetSettings = z.infer<
  typeof TeamworkCommerceWorksheetSettingsSchema
>;

export const TeamworkCommerceGiftCardSettingsSchema = z.object({
  numberPrefix: code.regex(/^\d+$/),
  numberLength: z.number().int().min(12).max(40),
});

export const TeamworkCommerceProcessingSettingsSchema = z
  .object({
    enabled: z.boolean(),
    customOrders: z.object({
      paymentGateways: z.array(code),
      manualReviewPaymentGateways: z.array(code),
      refundDocument: z.enum(TeamworkCommerceDocumentKind),
    }),
    storeCredit: z.object({
      issuer: z.enum(TeamworkCommerceStoreCreditIssuer),
      giftCards: TeamworkCommerceGiftCardSettingsSchema.optional(),
    }),
    receipt: TeamworkCommerceReceiptSettingsSchema.optional(),
    worksheet: TeamworkCommerceWorksheetSettingsSchema.optional(),
  })
  .superRefine((settings, ctx) => {
    if (!settings.enabled) {
      return;
    }
    if (!settings.receipt) {
      ctx.addIssue({
        code: "custom",
        message: "Teamwork return processing requires return receipt settings.",
        path: ["receipt"],
      });
    }
    if (
      settings.customOrders.refundDocument ===
        TeamworkCommerceDocumentKind.WORKSHEET &&
      (!settings.worksheet || !settings.customOrders.paymentGateways.length)
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "Worksheet refunds require worksheet settings and the custom-order payment methods Teamwork refunds.",
        path: ["worksheet"],
      });
    }
    const giftCards = settings.storeCredit.giftCards;
    if (
      settings.storeCredit.issuer ===
        TeamworkCommerceStoreCreditIssuer.TEAMWORK &&
      !giftCards
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Teamwork gift cards require gift card settings.",
        path: ["storeCredit", "giftCards"],
      });
    }
    if (
      giftCards &&
      giftCards.numberLength - giftCards.numberPrefix.length < 10
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "Gift card numbers need at least ten generated digits after the prefix.",
        path: ["storeCredit", "giftCards", "numberLength"],
      });
    }
  });

export type TeamworkCommerceProcessingSettings = z.infer<
  typeof TeamworkCommerceProcessingSettingsSchema
>;

export function matchesTeamworkPaymentGateways({
  paymentGateways,
  configuredGateways,
}: {
  paymentGateways: readonly string[];
  configuredGateways: readonly string[];
}): boolean {
  const expected = new Set(
    configuredGateways.map((gateway) => gateway.trim().toLowerCase()),
  );
  return (
    paymentGateways.length > 0 &&
    paymentGateways.every((gateway) =>
      expected.has(gateway.trim().toLowerCase()),
    )
  );
}
